import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import { Button, Heading, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";

import InvoiceStatus from "./InvoiceStatus";
import ErrorText from "components/API/ErrorText";
import useApiRequest from "hooks/api/useApiHook";
import { formatDate } from "utils/conversion/formatTime";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";

const BalanceHistory = () => {
  const apiKey = useSelector((state: RootState) => state.apiKey.apiKey);
  const navigate = useNavigate();

  // Load invoice data (top-ups)
  const { data: invoicesData, loading: invoicesLoading, error: invoicesError } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.GET_INVOICES_HISTORY,
    apiToken: apiKey,
  });

  // Load purchase data
  const { data: purchasesData, loading: purchasesLoading, error: purchasesError } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.GET_PURCHASES,
    apiToken: apiKey,
  });

  // Function to combine and sort data
  const getCombinedData = () => {
    const combinedData = [];

    // Add invoices (top-ups)
    if (invoicesData?.data && Array.isArray(invoicesData.data)) {
      invoicesData.data.forEach(invoice => {
        combinedData.push({
          ...invoice,
          type: 'invoice',
          sortDate: new Date(invoice.creation_time || invoice.created_at || invoice.created),
        });
      });
    }

    // Add purchases
    if (purchasesData && Array.isArray(purchasesData)) {
      purchasesData.forEach(purchase => {
        combinedData.push({
          ...purchase,
          type: 'purchase',
          sortDate: new Date(purchase.date),
        });
      });
    } else if (purchasesData && purchasesData.status === 'success' && Array.isArray(purchasesData.data)) {
      purchasesData.data.forEach(purchase => {
        combinedData.push({
          ...purchase,
          type: 'purchase',
          sortDate: new Date(purchase.date),
        });
      });
    } else if (purchasesData && purchasesData.status === 'success' && purchasesData.data?.purchases && Array.isArray(purchasesData.data.purchases)) {
      purchasesData.data.purchases.forEach(purchase => {
        combinedData.push({
          ...purchase,
          type: 'purchase',
          sortDate: new Date(purchase.date),
        });
      });
    }
    
    // Sort by date (newest first)
    return combinedData.sort((a, b) => b.sortDate - a.sortDate);
  };

  const renderRow = (row) => {
    const isInvoice = row.type === 'invoice';

    // Determine operation type and description
    const getOperationInfo = () => {
      if (isInvoice) {
        const network = row.currency?.toUpperCase() || 'Unknown';
        return {
          type: '💰 Top-up',
          description: `${network} top-up`,
          amount: row.amount_usd || row.amount || row.usd_amount || 0,
          status: row.invoice_status || row.status
        };
      } else {
        return {
          type: '🛒 Purchase',
          description: row.product_name_en || 'Subscription',
          amount: Math.abs(row.amount_usd || 0), // Take absolute value
          status: 'completed'
        };
      }
    };

    const operation = getOperationInfo();

    return (
      <Tr
        key={isInvoice ? (row.ugate_invoice_id || row.invoice_id || row.id) : row.deal_id}
        cursor="pointer"
        _hover={{ background: "bg.surfaceHover" }}
        className="balance-history-table-row"
      >
        {/* Operation type */}
        <Td>
          <span style={{ 
            color: isInvoice ? 'var(--ips-primary-strong)' : 'var(--ips-success-strong)',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            {operation.type}
          </span>
        </Td>
        
        {/* Description */}
        <Td>
          <span style={{ fontSize: '14px' }}>
            {operation.description}
          </span>
        </Td>
        
        {/* Amount */}
        <Td>
          <span style={{ 
            color: isInvoice ? 'var(--ips-primary-strong)' : 'var(--ips-error)',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            {isInvoice ? '+' : '-'}${operation.amount}
          </span>
        </Td>
        
        {/* Date */}
        <Td>
          <span style={{ fontSize: '14px' }}>
            {isInvoice 
              ? formatDate(row.creation_time || row.created_at || row.created)
              : formatDate(row.date)
            }
          </span>
        </Td>
        
        {/* Status */}
        <Td>
          {isInvoice ? (
            <InvoiceStatus status={operation.status} />
          ) : (
            <span style={{ color: 'var(--ips-success-strong)', fontWeight: 'bold', fontSize: '12px' }}>
              ✅ Completed
            </span>
          )}
        </Td>
        
        {/* Actions */}
        {isInvoice && ["waiting"].includes(operation.status) && (
          <Td pl={1}>
            <Button
              size="sm"
              onClick={() => navigate(`/invoice/${row.ugate_invoice_id || row.invoice_id || row.id}`)}
            >
              <FaRegEye />
            </Button>
          </Td>
        )}
      </Tr>
    );
  };

  const renderTable = () => {
    const combinedData = getCombinedData();
    
    return (
      <TableContainer maxHeight="400px" overflowY="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Type</Th>
              <Th>Description</Th>
              <Th>Amount</Th>
              <Th>Date</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>{combinedData.map(renderRow)}</Tbody>
        </Table>
      </TableContainer>
    );
  };

  const combinedData = getCombinedData();
  const isLoading = invoicesLoading || purchasesLoading;
  const hasError = invoicesError || purchasesError;
  const hasData = combinedData.length > 0;

  return (
    hasData && (
      <BoxShadowContainer className="balance-history-container">
        <Heading as="h3" size="md" mb={5}>
          📊 Balance History
        </Heading>
        {!isLoading ? (
          renderTable()
        ) : hasError ? (
          <ErrorText />
        ) : (
          <LoaderSpinner />
        )}
      </BoxShadowContainer>
    )
  );
};

export default BalanceHistory;
